import { DateTime } from 'luxon';
import styles from '../../styles/components/Calendar.module.scss';
import ArrowDownIcon from '../../assets/icons/arrow-down.svg';
import ChevronCroppedIcon from '../../assets/icons/chevron-cropped.svg';

const WEEKDAY_TITLES = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

type ViewMode = 'week' | 'month';

interface Props {
  selectedDate?: DateTime;
  onDateChange: (date: DateTime) => void;
  minDate?: DateTime;
  maxDate?: DateTime; 
  availableDates?: DateTime[];
  viewMode?: ViewMode;
  toggleViewMode?: () => void;
  onNavigate: (date: DateTime) => void;
  currentViewDate: DateTime;
}

export const Calendar: React.FC<Props> = ({
  selectedDate,
  onDateChange,
  minDate = DateTime.now().startOf('day'),
  maxDate,
  availableDates,
  viewMode = 'month',
  toggleViewMode,
  onNavigate,
  currentViewDate
}) => {

  // Determine start date for the current view (week/month)
  const startDate = viewMode === 'week'
  ? currentViewDate.minus({ days: currentViewDate.weekday % 7 }) // Forces Sunday as the start of the week
  : currentViewDate.startOf('month');

  // Calculate days to show based on view mode
  const numDays = viewMode === 'week' ? 7 : currentViewDate.daysInMonth ?? 30;
  const daysToShow =
    viewMode === 'week'
      ? Array.from({ length: 7 }, (_, i) => startDate.plus({ days: i }))
      : (() => {
          const firstDayOfMonth = currentViewDate.startOf('month');
          const firstDayWeekday = firstDayOfMonth.weekday % 7;
          const daysBefore = Array.from({ length: firstDayWeekday }, (_, i) =>
            firstDayOfMonth.minus({ days: firstDayWeekday - i })
          );
          const daysInMonth = Array.from({ length: numDays }, (_, i) =>
            firstDayOfMonth.plus({ days: i })
          );
          const totalDays = [...daysBefore, ...daysInMonth];
          const remainingSlots = totalDays.length % 7 === 0 ? 0 : 7 - (totalDays.length % 7);
          const daysAfter = Array.from({ length: remainingSlots }, (_, i) =>
            daysInMonth[daysInMonth.length - 1].plus({ days: i + 1 })
          );
          return [...daysBefore, ...daysInMonth, ...daysAfter];
        })();

  const today = DateTime.now().startOf('day');

  const isPastDate = (date: DateTime) => date < today.startOf('day') && !date.hasSame(today, 'day');

  const isDateSelectable = (date: DateTime) => {
    if (minDate && date < minDate.startOf('day')) return false;
    if (maxDate && date > maxDate.startOf('day')) return false;
    if (availableDates && availableDates.length) return availableDates.some((d) => d.hasSame(date, 'day'));
    return true;
  };

  const handlePrevious = () => {
    const prevDate = currentViewDate.minus(viewMode === 'week' ? { weeks: 1 } : { months: 1 });
  
    const prevEndDate = viewMode === 'week'
      ? prevDate.plus({ days: 6 }).endOf('day')
      : prevDate.endOf('month');
  
    if (!minDate || prevEndDate >= minDate.startOf('day')) {
      onNavigate(prevDate);
    }
  };

  const handleNext = () => {
    const nextDate = currentViewDate.plus(viewMode === 'week' ? { weeks: 1 } : { months: 1 });
    if (!maxDate || nextDate <= maxDate.startOf(viewMode)) {
      onNavigate(nextDate);
    }
  };

  const isPrevDisabled = () => {
    const prevDate = currentViewDate.minus(viewMode === 'week' ? { weeks: 1 } : { months: 1 });
    const prevEndDate = viewMode === 'week'
      ? prevDate.plus({ days: 6 }).endOf('day')
      : prevDate.endOf('month');
  
    return minDate ? prevEndDate < minDate.startOf('day') : false;
  };

  return (
    <div className={styles.dayPicker}>
      <div className={styles.container}>

        <div className={styles.header}>
          <button
            className={`${styles.navButton} ${styles.navButtonLeft}`}
            onClick={handlePrevious}
            disabled={isPrevDisabled()}
          >
            <img src={ArrowDownIcon} alt="arrow left icon" />
          </button>

          <div className={styles.headerCenterContent}>
            <p>{currentViewDate.toFormat('MMMM yyyy')}</p>
            <button className={styles.expandButton} onClick={toggleViewMode}>
              <img className={viewMode === 'month' ? styles.expanded : ''} src={ChevronCroppedIcon} alt="arrow down icon" />
            </button>
          </div>

          <button
            className={`${styles.navButton} ${styles.navButtonRight}`}
            onClick={handleNext}
            disabled={maxDate && currentViewDate.startOf(viewMode) >= maxDate.startOf(viewMode)}
          >
            <img src={ArrowDownIcon} alt="arrow right icon" />
          </button>
        </div>

        <div className={styles.weekDayTitles}>
          {WEEKDAY_TITLES.map((day, index) => (
            <div className={`${styles.tile} ${styles.weekDayTile}`} key={index}>
              {day}
            </div>
          ))}
        </div>

        <div className={styles.daysContainer}>
          {daysToShow.map((day, index) => {
            const isSelected = selectedDate && day.hasSame(selectedDate, 'day');
            const isToday = day.hasSame(today, 'day');
            const isDisabled = !isDateSelectable(day);
            const isPast = isPastDate(day);

            return (
              <div
                className={`${styles.tile} 
                  ${isSelected ? styles.selected : ''} 
                  ${isToday ? styles.today : ''} 
                  ${isPast ? styles.pastDate : ''} 
                  ${isDisabled ? styles.disabled : ''}`}
                key={index}
                onClick={() => !isDisabled && onDateChange(day)}
              >
                <span>{day.toFormat('d')}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
